import { extractBewilligung, type BewilligungForm } from '../lib/extractBewilligung'
import { ok, technical, type Result, type FieldId } from '@/core/lib/result'
import { buildDurchfuehrungsort } from '../lib/fillTb'
import { getAt, setAt } from '@/core/lib/path'
import { matchBewilligung } from '../lib/matchBewilligung'
import { getDocData, getDocBucketAddress, relocateDocObject, upsertDoc } from '@/core/data/dokumente'
import { uploadFile, removeFile, docPath, extFromFile, versionedFile } from '@/core/data/storage'
import {
  setAufgabe,
  getParticipant,
  getTeilnahmeDates,
  getCoachingVoucherRef,
  updateCoachingVoucherFields,
  updateCoachingOrgZeichen,
  listBewilligungCandidates,
  type AdvanceResult,
  type BewilligungCandidate,
} from '@/core/data/coachings'
import { getMassnahmeSelectionByNummer } from '@/core/data/massnahmen'
import { getOrganisation } from '@/core/data/organisation'
import { me } from '@/core/data/session'
import { loadAvgs, saveAvgsDraft } from './avgs'
import { loadTbForm, saveTbForm } from './tb'
import { NO_UE_CAP } from '../lib/noUeCap'
import { computeAdoptionPatches } from './bewilligungAdopt'

import { KOSTENTRAEGER_FIELDS } from './bewilligungCheck'
export { checkBewilligung, checkKostentraeger, KOSTENTRAEGER_FIELDS } from './bewilligungCheck'
export type { BewilligungCheck, KostentraegerCheck } from './bewilligungCheck'

const BEWILLIGUNG = 'bewilligung'

export async function getBewilligungPath(coachingId: string): Promise<string | null> {
  return getDocBucketAddress(coachingId, BEWILLIGUNG)
}

const s = (v: unknown) => (v == null ? '' : String(v))

export async function applyBewilligungUpload(
  coachingId: string,
  file: File,
): Promise<Result<BewilligungForm>> {
  const prevPath = await getDocBucketAddress(coachingId, BEWILLIGUNG)
  let path = ''
  try {
    path = await uploadFile(file, versionedFile('bewilligung', extFromFile(file)))
    await upsertDoc(coachingId, BEWILLIGUNG, {
      daten: {},
      bucketAddress: path,
      fileType: file.type || 'application/pdf',
      fileSize: file.size,
    })
  } catch (e) {
    console.error("Bewilligung speichern fehlgeschlagen:", e)
    return technical("Bewilligung konnte nicht gespeichert werden.")
  }
  if (prevPath) await removeFile(prevPath).catch(() => {})

  const extracted = await extractBewilligung(path)
  if (!extracted.ok) return extracted
  const b = extracted.data
  try {
    await upsertDoc(coachingId, BEWILLIGUNG, { daten: { extraction: b } })
  } catch (e) {
    console.error("Bewilligung speichern fehlgeschlagen:", e)
    return technical("Bewilligung konnte nicht gespeichert werden.")
  }
  try {
    await setAufgabe(coachingId, 'bewilligungPruefen')
  } catch (e) {
    console.error("Aufgabe \"bewilligungPruefen\" konnte nicht gesetzt werden:", e)
  }
  return ok(b)
}

export type BewilligungAutoMatchFailure =
  | { kind: 'no-match'; nachname: string | null }
  | { kind: 'ambiguous'; nachname: string }
  | { kind: 'already-uploaded'; vorname: string; nachname: string }
  | { kind: 'no-candidates' }
  | { kind: 'technical'; message: string }

export type BewilligungAutoMatchResult =
  | { ok: true; coachingId: string }
  | ({ ok: false } & BewilligungAutoMatchFailure)

export async function applyBewilligungUploadAutoMatch(file: File): Promise<BewilligungAutoMatchResult> {
  const candidates = await listBewilligungCandidates()
  if (candidates.length === 0) {
    return { ok: false, kind: 'no-candidates' }
  }

  const fileName = versionedFile('bewilligung', extFromFile(file))

  let stagePath = ''
  try {
    stagePath = await uploadFile(file, fileName)
  } catch (e) {
    console.error("Bewilligung speichern fehlgeschlagen:", e)
    return { ok: false, kind: 'technical', message: "Bewilligung konnte nicht gespeichert werden." }
  }

  let extracted: Result<BewilligungForm>
  try {
    extracted = await extractBewilligung(file)
  } catch (e) {
    await removeFile(stagePath).catch(() => {})
    throw e
  }
  if (!extracted.ok) {
    await removeFile(stagePath).catch(() => {})
    return {
      ok: false,
      kind: 'technical',
      message: extracted.kind === 'technical' ? extracted.message : "Bewilligungs-Erkennung fehlgeschlagen.",
    }
  }
  const b = extracted.data
  const nachname = getAt(b, 'Teilnehmer.Nachname')
  const massnahmeNummer = getAt(b, 'Maßnahme.Nummer der Maßnahme')
  const match = matchBewilligung(
    { nachname: typeof nachname === 'string' ? nachname : null },
    typeof massnahmeNummer === 'string' ? massnahmeNummer : null,
    candidates,
  )

  if (match.kind === 'none') {
    await removeFile(stagePath).catch(() => {})
    return { ok: false, kind: 'no-match', nachname: typeof nachname === 'string' ? nachname : null }
  }
  if (match.kind === 'ambiguous') {
    await removeFile(stagePath).catch(() => {})
    return { ok: false, kind: 'ambiguous', nachname: typeof nachname === 'string' ? nachname : '' }
  }

  const matchedCandidate: BewilligungCandidate = match.candidate
  if (!matchedCandidate.ready) {
    await removeFile(stagePath).catch(() => {})
    return {
      ok: false,
      kind: 'already-uploaded',
      vorname: matchedCandidate.vorname,
      nachname: matchedCandidate.nachname,
    }
  }

  const matchedId = matchedCandidate.coachingId

  const prevPath = await getDocBucketAddress(matchedId, BEWILLIGUNG)
  try {
    await upsertDoc(matchedId, BEWILLIGUNG, {
      daten: { extraction: b },
      bucketAddress: stagePath,
      fileType: file.type || 'application/pdf',
      fileSize: file.size,
    })
  } catch (e) {
    console.error("Bewilligung speichern fehlgeschlagen:", e)
    await removeFile(stagePath).catch(() => {})
    return { ok: false, kind: 'technical', message: "Bewilligung konnte nicht gespeichert werden." }
  }

  const finalPath = await relocateDocObject(stagePath, docPath(matchedId, fileName))

  if (prevPath && prevPath !== finalPath) await removeFile(prevPath).catch(() => {})

  try {
    await setAufgabe(matchedId, 'bewilligungPruefen')
  } catch (e) {
    console.error("Aufgabe \"bewilligungPruefen\" konnte nicht gesetzt werden:", e)
  }

  return { ok: true, coachingId: matchedId }
}

export async function loadBewilligung(coachingId: string): Promise<BewilligungForm | null> {
  const d = await getDocData(coachingId, BEWILLIGUNG)
  if (!d?.extraction) return null
  const values = structuredClone(d.extraction) as BewilligungForm
  for (const field of KOSTENTRAEGER_FIELDS) {
    const saved = getAt(d.form, field)
    if (typeof saved === 'string' && saved.trim() !== '') setAt(values, field, saved.trim())
  }
  return values
}

export async function confirmBewilligung(coachingId: string): Promise<AdvanceResult> {
  return setAufgabe(coachingId, 'ecnFuehren')
}

export async function buildBewilligungReference(coachingId: string): Promise<BewilligungForm> {
  const [participant, voucher, dates, org] = await Promise.all([
    getParticipant(coachingId),
    getCoachingVoucherRef(coachingId),
    getTeilnahmeDates(coachingId),
    me().then(({ organisationId }) => getOrganisation(organisationId)),
  ])
  const maxUe = voucher?.zeitl_umf_max_ue ?? 0
  return {
    Kostenträger: {},
    Teilnehmer: {
      Vorname: s(participant?.vorname),
      Nachname: s(participant?.nachname),
    },
    Maßnahme: {
      'Nummer der Maßnahme': s(voucher?.massnahme?.nummer),
      'Maßnahmeinhalt (Gruppenmaßnahme)': s(voucher?.inhalt_gruppen),
      'Maßnahmeinhalt (Einzelmaßnahme)': s(voucher?.inhalt_einzel),
      'Durchführungsort': buildDurchfuehrungsort(org),
      'Beginn der Maßnahme': s(dates.beginn ?? ''),
      'Ende der Maßnahme': s(dates.ende ?? ''),
      'Zeitlicher Umfang (Kategorie)': s(voucher?.zeitl_umf_kategorie),
      'Zeitlicher Umfang (max UE)':
        maxUe > 0 && maxUe !== NO_UE_CAP ? String(maxUe) : '',
    },
  }
}

export type AdoptResult = { ok: true } | { ok: false; reason: 'massnahme-not-found' }

export async function adoptBewilligung(
  coachingId: string,
  extracted: BewilligungForm,
  adoptable: FieldId[],
): Promise<AdoptResult> {
  let massnahmeSelection: string | undefined
  if (adoptable.includes('Maßnahme.Nummer der Maßnahme')) {
    const nummer = s(getAt(extracted, 'Maßnahme.Nummer der Maßnahme'))
    const selection = await getMassnahmeSelectionByNummer(nummer)
    if (!selection) return { ok: false, reason: 'massnahme-not-found' }
    massnahmeSelection = selection
  }

  const patches = computeAdoptionPatches(extracted, adoptable, { massnahmeSelection })

  if (patches.avgs.length) {
    const avgs: Record<string, unknown> = (await loadAvgs(coachingId)) ?? {}
    for (const { path, value } of patches.avgs) setAt(avgs, path, value)
    await saveAvgsDraft(coachingId, avgs)
  }
  if (patches.tb.length) {
    const tb: Record<string, unknown> = (await loadTbForm(coachingId)) ?? {}
    for (const { path, value } of patches.tb) setAt(tb, path, value)
    await saveTbForm(coachingId, tb)
  }
  if (Object.keys(patches.coaching).length) {
    await updateCoachingVoucherFields(coachingId, patches.coaching)
  }
  return { ok: true }
}

export type KostentraegerFields = {
  strasse: string
  hausnummer: string
  plz: string
  ort: string
  zeichen: string
}

export async function persistKostentraeger(coachingId: string, fields: KostentraegerFields): Promise<void> {
  const existing = await getDocData(coachingId, BEWILLIGUNG)
  if (!existing) throw new Error("Bewilligung konnte nicht geladen werden.")
  const form = existing?.form
  const previousForm = form && typeof form === 'object' && !Array.isArray(form) ? form : {}
  const kostentraeger = {
    Straße: fields.strasse.trim(),
    Hausnummer: fields.hausnummer.trim(),
    PLZ: fields.plz.trim(),
    Ort: fields.ort.trim(),
    Zeichen: fields.zeichen.trim(),
  }
  await upsertDoc(coachingId, BEWILLIGUNG, {
    daten: { ...existing, form: { ...previousForm, Kostenträger: kostentraeger } },
  })
  await updateCoachingOrgZeichen(coachingId, kostentraeger.Zeichen || null)
}
